
import { IEvent } from './IEvent';
import { IClonedEvent } from './IClonedEvent';
import { ClonedEventType as ClonedEventTypeEnum, ValidationErrorType } from './EventBase';
import { deepCopy } from '@ms/utilities-objects/lib/deepCopy';

const maxBufferSize = 100;

declare const window: Window & {
    ODSP_TELEMETRY_MANAGER?: IManager;
};

// Note: can be window or WorkerGlobalScope(in worker context).
declare const self: Window & {
    ODSP_TELEMETRY_MANAGER?: IManager;
};

function getStartDate(): Date {
    if (window.performance && window.performance.timing && window.performance.timing.navigationStart) {
        return new Date(window.performance.timing.navigationStart);
    } else {
        return new Date();
    }
}

export interface ILogHandler {
    (event: IClonedEvent): void;
}

/**
 * Represents a manager for telemetry events.
 */
export interface IManager {
    /**
     * The Date representation of the beginning of logging, which is expected to be
     * the moment the page was loaded.
     */
    readonly startDate: Date;
    /**
     * The numeric representation of startDate.
     */
    readonly startTime: number;
    /**
     * Gets the current time.
     */
    getTime(): number;
    /**
     * Cleans a string for logging.
     * @deprecated Individual log handlers should do this themselves.
     */
    cleanString(str: string, key?: string): string | undefined;
    /**
     * Registers a callback for an event handler.
     * The moment the handler is attached, the manager provides all buffered events to the handler.
     */
    addLogHandler(handler: ILogHandler): IClonedEvent[];
    /**
     * Unregisters a callback for an event handler.
     * This must be the same function instance passed to addLogHandler.
     */
    removeLogHandler(handler: ILogHandler): void;
    /**
     * Logs an event, dispatching it to all registered handlers. Used by Event implementations.
     */
    logEvent(event: IEvent, eventType: ClonedEventTypeEnum): void;
    /**
     * Registers a handler for validation errors from other logged events.
     */
    addValidationErrorLogger(validationLogger: (event: IEvent, type: ValidationErrorType) => void): void;
    /**
     * Removes a callback registered by addValidationErrorLoger.
     */
    removeValidationErrorLogger(validationLogger: (event: IEvent, type: ValidationErrorType) => void): void;
    /**
     * Logs a validation error encountered while logging another event.
     */
    logValidationError(event: IEvent, type: ValidationErrorType): void;
    /**
     * Determines ig a given feature is enabled.
     * @param feature A specification for a client-side feature.
     */
    isFeatureEnabled(feature: {}): boolean;
    /**
     * Registers a callback which determines whether the feature controlling an event is enabled.
     */
    addFeatureEnabler(featureEnabler: (feature: {}) => boolean): void;
    /**
     * Removes a callback registered by addFeatureEnabler.
     */
    removeFeatureEnabler(featureEnabler: (feature: {}) => boolean): void;
    /**
     * Gets a current stack trace.
     */
    getStack(): string | undefined;
}

class PrivateManager implements IManager {
    public get startDate(): Date {
        return getStartDate();
    }

    public get startTime(): number {
        const startDate = this.startDate;

        return startDate.getTime();
    }

    private _handlers: ((e: IClonedEvent) => void)[];
    private _buffer: IClonedEvent[];
    private _validationLoggers: ((event: IEvent, type: ValidationErrorType) => void)[];
    private _featureEnablers: ((feature: {}) => boolean)[];

    constructor() {
        this._handlers = [];
        this._buffer = [];
        this._validationLoggers = [];
        this._featureEnablers = [];
    }

    public getTime(): number {
        return (new Date()).getTime();
    }

    /** This is the overrideable clean string function. Return undefined to drop a parameter altogether. */
    public cleanString(str: string): string | undefined {
        return str;
    }

    public addLogHandler(handler: (e: IClonedEvent) => void): IClonedEvent[] {
        const handlers = this._handlers;
        const buffer = this._buffer;

        handlers.push(handler);

        // Return the buffer so the handler can get missed events
        return buffer.slice();
    }

    public removeLogHandler(handler: (e: IClonedEvent) => void): void {
        const handlers = this._handlers;

        for (let x = 0; x < handlers.length; x++) {
            if (handlers[x] === handler) {
                // Remove the handler
                handlers.splice(x, 1);
                break;
            }
        }
    }

    public logEvent(event: IEvent, eventType: ClonedEventTypeEnum): void {
        const buffer = this._buffer;
        const handlers = this._handlers;

        // Clone the object (do it natively because the browser can mark it as a type)
        const clonedEvent: IClonedEvent = Object.freeze({
            data: deepCopy(event.data),
            context: { ...event.context },
            id: event.id,
            enabled: event.enabled,
            critical: event.critical,
            endTime: event.endTime,
            eventName: event.eventName,
            shortEventName: event.shortEventName,
            parentId: event.parentId,
            startTime: event.startTime,
            eventType: eventType,
            metadata: event.metadata,
            vector: event.vector,
            validationErrors: event.validationErrors,
            isEventTypePrefixingDisabled: event.isEventTypePrefixingDisabled
        });

        // Add to the buffer
        buffer.push(clonedEvent);

        if (buffer.length > maxBufferSize) {
            buffer.splice(1, Infinity);

            // Make sure we have a handler before the buffer is overrun
            if (handlers.length === 0) {
                _handleBaseLoggingError(new Error('Logging buffer overflow hit before any logging handler was registered'));
            }
        }

        // Let handlers know the log event has completed
        for (let x = 0; x < handlers.length; x++) {
            try {
                handlers[x](clonedEvent);
            } catch (e) {
                _handleBaseLoggingError(e);
            }
        }
    }

    public addValidationErrorLogger(validationLogger: (event: IEvent, type: ValidationErrorType) => void): void {
        const validationLoggers = this._validationLoggers;

        validationLoggers.push(validationLogger);
    }

    public removeValidationErrorLogger(validationLogger: (event: IEvent, type: ValidationErrorType) => void): void {
        const validationLoggers = this._validationLoggers;

        const index = validationLoggers.indexOf(validationLogger);

        if (index > -1) {
            validationLoggers.splice(index, 1);
        }
    }

    public logValidationError(event: IEvent, type: ValidationErrorType): void {
        const validationLoggers = this._validationLoggers;

        for (const validationLogger of validationLoggers) {
            try {
                validationLogger(event, type);
            } catch (e) {
                _handleBaseLoggingError(e);
            }
        }
    }

    public isFeatureEnabled(feature: {}): boolean {
        const featureEnablers = this._featureEnablers;

        for (const featureEnabler of featureEnablers) {
            if (featureEnabler(feature)) {
                return true;
            }
        }

        return false;
    }

    public addFeatureEnabler(featureEnabler: (feature: {}) => boolean): void {
        const featureEnablers = this._featureEnablers;

        featureEnablers.push(featureEnabler);
    }

    public removeFeatureEnabler(featureEnabler: (feature: {}) => boolean): void {
        const featureEnablers = this._featureEnablers;

        const index = featureEnablers.indexOf(featureEnabler);

        if (index > -1) {
            featureEnablers.splice(index, 1);
        }
    }

    public getStack(): string | undefined {
        let error: Error | undefined;

        try {
            let w: Window & {
                ______ExpectedError______?(): void;
            } = window;
            if (w.______ExpectedError______) {
                w.______ExpectedError______();
            }
        } catch (e) {
            error = e;
        }

        return error && error.stack || undefined;
    }
}

/**
 * Creates a new, private manager with its own event queue and handlers.
 */
export function createManager(): IManager {
    return new PrivateManager();
}

function createDefaultGlobalManager(): IManager {
    let globalManager: IManager;
    if (typeof window !== 'undefined') {
        globalManager = window.ODSP_TELEMETRY_MANAGER = window.ODSP_TELEMETRY_MANAGER || createManager();
    } else if (typeof self !== 'undefined') {
        globalManager = self.ODSP_TELEMETRY_MANAGER = self.ODSP_TELEMETRY_MANAGER || createManager();
    } else {
        globalManager = createManager();
    }
    return globalManager;
}

/**
 * Gets the default/fallback telemetry manager for the running application.
 */
export const Manager: IManager = createDefaultGlobalManager();

function _handleBaseLoggingError(error: Error): void {
    // TODO: Log somewhere
}
